import {
    ENTITY_CRUD_CLOSE,
    ENTITY_CRUD_INIT_WORKFLOW,
    ENTITY_DETAIL_CHANGE_SECTION,
    ENTITY_DETAIL_CLOSE,
    ENTITY_DETAIL_INIT,
    ENTITY_DETAIL_INIT_ERROR,
    ENTITY_DETAIL_INIT_SUCCESS,
    ENTITY_DETAIL_LOADING,
    ENTITY_DETAIL_PREFETCH_SUCCESS,
    ENTITY_DETAIL_RESET,
    ENTITY_DETAIL_RESET_NEXT_DETAIL,
    ENTITY_DETAIL_SET_NEXT_DETAIL,
    ENTITY_DETAIL_SET_TABS,
    ENTITY_UPDATE_EXTRA,
    ENTITY_UPDATE_LOCALLY,
    ENTITY_DETAIL_OPEN,
} from 'constants/ActionTypes';
import { ACTIVITIES, CAMPAIGNS_MARKETING, COMPANIES } from 'constants/Entities';
import Context from 'managers/Context';
import { ensureRoute, backRouteWorkflow, getCurrentPath } from 'utils/routes';
import { getEntityFromString } from 'utils/getEntityFromString';
import { logEvent, trackEvent } from 'utils/tracking';
import * as SatismeterUtils from 'utils/satismeter';
import { isEntityEnvironmentAllowed } from 'utils/multiEnvironment';
import { isBackendFalsy } from '@Sage/ssm-utils/backendScalars';
import {
    buildEntityDetailExtraId,
    buildEntityDetailTabKey,
    resolveEntityDetailExtraId,
} from 'utils/entityDetailTabKey';

import { EVENT_TYPES } from 'constants/Constants';

const getActivityTypeFromInfo = (info) => info?.activityType ?? info?.ActivityType ?? null;

const getDetailKeyOptions = (entity, info, origin) => {
    const activityType =
        entity?.entity === ACTIVITIES.entity ? getActivityTypeFromInfo(info) : undefined;
    return {
        activityType,
        origin: origin || undefined,
        parentEntityContext:
            entity?.entity === ACTIVITIES.entity
                ? info?.parentEntityContext || undefined
                : undefined,
    };
};

/** In-flight company prefetches (avoids duplicate GET when hooks mount twice). */
const companyPrefetchInFlight = new Set();

export function initEntityDetail(
    entity,
    id,
    hasCrudInDetail = false,
    info,
    avoidRedirects = false,
    origin = null,
) {
    return (dispatch, getState) =>
        new Promise((resolve, reject) => {
            const keyOptions = getDetailKeyOptions(entity, info, origin);
            const extraId = buildEntityDetailExtraId(keyOptions);

            Context.entityDetailManager
                .init(entity, id, info)
                .then(({ data, rawData }) => {
                    if (!data) {
                        dispatch({
                            type: ENTITY_DETAIL_INIT_SUCCESS,
                            data: {},
                            entity: entity.entity,
                            id,
                            extraId,
                        });
                        resolve();
                        return;
                    }

                    if (!isEntityEnvironmentAllowed(getState(), entity, data)) {
                        dispatch({
                            type: ENTITY_DETAIL_INIT_ERROR,
                            error: { environmentNotAllowed: true },
                            entity: entity.entity,
                            id,
                            extraId,
                        });
                        reject({ environmentNotAllowed: true });
                        return;
                    }

                    const dispatchInitSuccess = () => {
                        dispatch({
                            type: ENTITY_DETAIL_INIT_SUCCESS,
                            data,
                            entity: entity.entity,
                            id,
                            extraId,
                        });
                    };

                    if (hasCrudInDetail) {
                        dispatch(
                            Context.actions.EntityCrudActions.init({
                                entity,
                                id,
                                extraId,
                                isBulkAction: false,
                                isFromDetail: true,
                                data: rawData,
                                isFromTab: Object.entries(getState().entityDetail.tabs).length > 1,
                                avoidRedirects,
                            }),
                        )
                            .then(() => {
                                dispatchInitSuccess();
                                resolve();
                            })
                            .catch((err) => {
                                console.error(err);
                                dispatchInitSuccess();
                                resolve();
                            });
                    } else {
                        dispatchInitSuccess();
                        resolve();
                    }
                })
                .catch((errors) => {
                    console.error(errors);
                    reject(errors);
                });
        });
}

export function open(
    entity,
    id,
    hasCrudInDetail = false,
    extra,
    forceRefresh = false,
    avoidOpenDrawer = false,
    info = null,
    toTab = false,
    avoidRedirects = false,
    origin = null,
) {
    return (dispatch, getState) =>
        new Promise((resolve) => {
            const entityName = entity.entity;
            const entityDetail = getState().entityDetail;
            const keyOptions = getDetailKeyOptions(entity, info, origin);
            const extraId = buildEntityDetailExtraId(keyOptions);
            const tabId = buildEntityDetailTabKey(entityName, id, keyOptions);
            const activeDetail = entityDetail?.[entityDetail?.active];
            const isSameDetail =
                activeDetail?.entity === entityName &&
                String(activeDetail?.id) === String(id) &&
                String(activeDetail?.extraId ?? '') === String(extraId ?? '');
            const tabs = !toTab ? {} : entityDetail?.tabs || {};

            // Avoid duplicated tabs
            if (!tabs[tabId]) tabs[tabId] = { primary: Object.keys(tabs).length === 0 };

            if (isSameDetail && !forceRefresh && !entityDetail.purge) {
                // Executed when you come back from other page
                // Avoiding detail grid rendering problem...
                dispatch({
                    type: ENTITY_DETAIL_LOADING,
                    entity: entityName,
                    loading: true,
                    id,
                    extraId,
                    extra,
                });

                !avoidOpenDrawer &&
                    dispatch({ type: ENTITY_DETAIL_OPEN, entity, id, extraId, extra, tabs });

                setTimeout(() => {
                    dispatch({
                        type: ENTITY_DETAIL_LOADING,
                        entity: entityName,
                        loading: false,
                        id,
                        extraId,
                        extra,
                    });
                }, 500);
                resolve();
                return;
            }

            dispatch({
                type: ENTITY_DETAIL_INIT,
                entity: entityName,
                route: entity.route || entityName,
                id,
                extraId,
                origin: origin || null,
                parentEntityContext: info?.parentEntityContext || null,
                parentEntityFilter: info?.parentEntityFilter || null,
                hasCrudInDetail,
                extra,
                tabs,
                avoidRedirects,
            });

            dispatch(initEntityDetail(entity, id, hasCrudInDetail, info, avoidRedirects, origin))
                .then(() => {
                    resolve();
                    // Marketing campaigns: no objectTypeId (trackEvent unsupported) and detailView is
                    // logged in CampaignDetails with submodule + sent-status guard.
                    if (entity.entity !== CAMPAIGNS_MARKETING.entity) {
                        logEvent({
                            event: entity.trueName,
                            functionality: 'detailView',
                        });
                        trackEvent({ entity, entityId: id, idEventType: EVENT_TYPES.read });
                    }

                    if (toTab) {
                        SatismeterUtils.track('newNavigationSystem');
                    }
                })
                .catch((errors) => {
                    console.error(errors);
                    dispatch({
                        type: ENTITY_DETAIL_INIT_ERROR,
                        errors,
                        entity: entityName,
                        id,
                        extraId,
                    });
                });
        });
}

export function hiddenRefresh(entity, id) {
    return (dispatch, getState) => {
        // maybe a new action to put loadingWidgets to true ?
        const entityDetail = getState().entityDetail;
        const activeDetail = entityDetail[entityDetail.active];
        const activityType =
            entity.entity === ACTIVITIES.entity
                ? (activeDetail?.data?.ActivityType ??
                  parseExtraIdActivityType(activeDetail?.extraId))
                : undefined;
        const origin = activeDetail?.origin || undefined;
        const tabToRefresh = buildEntityDetailTabKey(entity.entity, id, {
            activityType,
            parentEntityContext: activeDetail?.parentEntityContext || undefined,
        });
        const active = entityDetail.active === tabToRefresh ? entityDetail.active : tabToRefresh;
        const hasCrudInDetail = (entityDetail && entityDetail[active]?.hasCrudInDetail) || false;
        const info =
            entity.entity === ACTIVITIES.entity && activityType
                ? {
                      activityType,
                      parentEntityContext: activeDetail?.parentEntityContext || null,
                      parentEntityFilter: activeDetail?.parentEntityFilter || null,
                  }
                : null;
        dispatch(initEntityDetail(entity, id, hasCrudInDetail, info, false, origin || null));
    };
}

const parseExtraIdActivityType = (extraId) => {
    if (!extraId) return null;
    return String(extraId).split('-')[0] || null;
};

export function prefetchCompanyForDetail(companyId) {
    return (dispatch, getState) => {
        if (isBackendFalsy(companyId)) return Promise.resolve();

        const prefetchKey = String(companyId);
        const tabKey = `${COMPANIES.entity}-${companyId}`;
        if (getState().entityDetail[tabKey]?.data) return Promise.resolve();
        if (companyPrefetchInFlight.has(prefetchKey)) return Promise.resolve();

        companyPrefetchInFlight.add(prefetchKey);
        const model = Context.entityManager.getModel(COMPANIES);

        return new Promise((resolve) => {
            Context.domainManager.getEntity(
                companyId,
                COMPANIES.entity,
                false,
                (entityData) => {
                    const data = model?.toDetail ? model.toDetail(entityData) : entityData;
                    if (data && isEntityEnvironmentAllowed(getState(), COMPANIES, data)) {
                        dispatch({ type: ENTITY_DETAIL_PREFETCH_SUCCESS, tabKey, data });
                    }
                    resolve();
                },
                () => resolve(),
            );
        }).finally(() => {
            companyPrefetchInFlight.delete(prefetchKey);
        });
    };
}

export function initWorkFlow() {
    return (dispatch) => {
        let workFlowPersist = sessionStorage.getItem('fm_workflow_crud_persist');
        if (workFlowPersist) {
            try {
                workFlowPersist = JSON.parse(workFlowPersist);
                let { data, entity, hasCrudInDetail, fieldsToUpdate } = workFlowPersist;
                dispatch(open(entity, data.id, hasCrudInDetail)).then(() => {
                    let workFlowEnd = sessionStorage.getItem('fm_workflow_action_end');
                    const toSave = workFlowEnd === 'true';
                    if (toSave) {
                        dispatch({
                            type: ENTITY_CRUD_INIT_WORKFLOW,
                            data,
                            loading: false,
                            loadingSave: toSave,
                        });

                        dispatch(
                            Context.actions.EntityCrudActions.save({
                                entity,
                                openDetail: false,
                                hasCrudInDetail: true,
                                fieldsToUpdate,
                                fromWorkFlow: true,
                            }),
                        );
                    } else {
                        // remove workflow word if we don't need to do anything more
                        ensureRoute(backRouteWorkflow());
                    }
                    sessionStorage.removeItem('fm_workflow_action_end');
                });
            } catch (e) {
                console.error(
                    'something went wrong trying to restore previous detail and saving it',
                    e,
                );
            }
        }
    };
}

export function refresh(entity) {
    return (dispatch, getState) => {
        const detailState = getState().entityDetail;

        if (
            detailState &&
            detailState.isOpen &&
            detailState[detailState.active] &&
            detailState[detailState.active].entity === entity.entity
        ) {
            const activeDetail = detailState[detailState.active];
            const hasCrudInDetail = activeDetail?.hasCrudInDetail || false;
            const activityType =
                entity.entity === ACTIVITIES.entity
                    ? (activeDetail?.data?.ActivityType ??
                      parseExtraIdActivityType(activeDetail?.extraId))
                    : undefined;
            const origin = activeDetail?.origin || null;
            const info =
                entity.entity === ACTIVITIES.entity && activityType
                    ? {
                          activityType,
                          parentEntityContext: activeDetail?.parentEntityContext || null,
                          parentEntityFilter: activeDetail?.parentEntityFilter || null,
                      }
                    : null;
            dispatch(
                open(
                    entity,
                    activeDetail.id,
                    hasCrudInDetail,
                    null,
                    true,
                    false,
                    info,
                    false,
                    false,
                    origin,
                ),
            );
        }
    };
}

export function deleteEntity(entity, activeEntity, isAccumulative) {
    return (dispatch, getState) =>
        new Promise((resolve, reject) => {
            const state = getState();
            let entityDetail = state.entityDetail;
            entityDetail = entityDetail[entityDetail.active];
            const entityList =
                state?.entityList?.[entityDetail?.entity] ||
                state?.entityList?.[state?.entityList?.active] ||
                null;
            const useLazyLoad = entityList?.useLazyLoad || false;
            let { active, tabs: finalTabs } = state.entityDetail;

            let route, entityToInit, relatedEntity;

            // When deleting an entity while in "Conversations" (the activeEntity)
            // We want to delete the entity but stay in "Conversations"
            if (activeEntity) {
                route = `${activeEntity.route}${entityDetail.route}`;
                entityToInit = activeEntity;
                relatedEntity = entity;
            } else {
                const isMapOpen = window.location.href.includes('/map/');
                route = isMapOpen ? `${entityDetail.route}/map` : entityDetail.route;
                entityToInit = entity;
                relatedEntity = null;
            }

            const tabKey = buildEntityDetailTabKey(entityDetail.entity, entityDetail.id, {
                activityType:
                    entityDetail.entity === ACTIVITIES.entity
                        ? (entityDetail?.data?.ActivityType ??
                          parseExtraIdActivityType(entityDetail?.extraId))
                        : undefined,
                parentEntityContext: entityDetail?.parentEntityContext || undefined,
            });
            const tab = finalTabs[tabKey] || finalTabs[state.entityDetail.active];
            const resolvedTabKey = finalTabs[tabKey] ? tabKey : state.entityDetail.active;

            const shouldUpdateTabs = tab && !tab.primary;

            if (shouldUpdateTabs) {
                let foundIndex = 0;

                finalTabs = Object.entries(finalTabs).reduce((obj, [key, value], index) => {
                    if (key !== resolvedTabKey) {
                        obj[key] = value;
                    } else {
                        foundIndex = index;
                    }
                    return obj;
                }, {});

                if (active === resolvedTabKey) {
                    if (foundIndex) foundIndex--;
                    active = Object.keys(finalTabs)[foundIndex];
                }
            }

            Context.entityDetailManager
                .delete(entity, entityDetail)
                .then((data) => {
                    if (shouldUpdateTabs) {
                        dispatch({
                            type: ENTITY_DETAIL_SET_TABS,
                            active,
                            tabs: finalTabs,
                        });
                        dispatch(
                            Context.actions.EntityActions.clearDataForChip(
                                entity.redux,
                                entityDetail.id,
                            ),
                        );
                        if (useLazyLoad) {
                            dispatch(
                                Context.actions.EntityListActions.init(
                                    entityToInit,
                                    false,
                                    null,
                                    null,
                                    null,
                                    useLazyLoad,
                                    null,
                                    null,
                                    isAccumulative,
                                    relatedEntity,
                                ),
                            );
                        } else {
                            dispatch(Context.actions.EntityListActions.init(entityToInit, true));
                        }
                    } else {
                        dispatch({ type: ENTITY_DETAIL_CLOSE, purge: true });
                        dispatch({ type: ENTITY_CRUD_CLOSE });
                        dispatch(
                            Context.actions.EntityListSelectActions.resetListSelect(entityToInit),
                        );
                        // useLazyLoad for w5 table
                        // make 2 possible calls because null removes default prop values
                        if (useLazyLoad) {
                            dispatch(
                                Context.actions.EntityListActions.init(
                                    entityToInit,
                                    false,
                                    null,
                                    null,
                                    null,
                                    useLazyLoad,
                                    null,
                                    null,
                                    isAccumulative,
                                    relatedEntity,
                                ),
                            );
                        } else {
                            dispatch(Context.actions.EntityListActions.init(entityToInit, true));
                        }
                    }

                    const id = entityDetail?.data?.id || '';
                    if (id) trackEvent({ entity, entityId: id, idEventType: EVENT_TYPES.delete });

                    resolve(data);
                    if (!shouldUpdateTabs && !entityDetail?.avoidRedirects) ensureRoute(route);
                })
                .catch((e) => {
                    console.error(e);
                    let serverError = !e || (e && e.serverError);

                    reject({ errors: e.errors, serverError });
                });
        });
}

export function close(props = {}) {
    return (dispatch, getState) => {
        // Ensuring current route
        const {
            purge = false,
            purgeTabs = false,
            tabs = {},
            isFromTab = false,
            primaryDetail,
        } = props;

        // Reset any errors when closing
        const state = getState();
        const activeDetail = state.entityDetail[state.entityDetail.active];
        if (activeDetail?.error) {
            dispatch({
                type: ENTITY_DETAIL_RESET,
            });
        }

        let entityDetail = state.entityDetail;
        entityDetail = entityDetail[primaryDetail || entityDetail.active];
        const isMapOpen = window.location.href.includes('/map/');
        const isGridOpen = window.location.href.includes('/grid/');

        // Check if we're going back to the list from a detail tab of
        // a different type and override the return route to the list
        const activeEntityList = getEntityFromString(state.entityList.active);
        let activeEntity =
            activeEntityList?.entity === entityDetail?.entity ? entityDetail : activeEntityList;

        // Sub-sections (e.g. marketing lists) have their own entity route while
        // entityList.active may still point at the parent section.
        const detailRoute = entityDetail?.route;
        const currentPath = getCurrentPath();
        if (
            detailRoute &&
            activeEntityList?.entity !== entityDetail?.entity &&
            typeof currentPath === 'string' &&
            (currentPath === detailRoute || currentPath.startsWith(`${detailRoute}/`))
        ) {
            activeEntity = entityDetail;
        }

        if (entityDetail && activeEntity && !isFromTab && !entityDetail?.avoidRedirects) {
            if (isMapOpen) {
                ensureRoute(`${activeEntity.route || activeEntity.entity}/map`);
            } else if (isGridOpen) {
                ensureRoute(`${activeEntity.route || activeEntity.entity}/grid`);
            } else {
                ensureRoute(`${activeEntity.route || activeEntity.entity}`);
            }
        }
        dispatch({ type: ENTITY_CRUD_CLOSE, tabs });
        dispatch({ type: ENTITY_DETAIL_CLOSE, purge, purgeTabs, tabs });
    };
}

export function openHidden() {
    return (dispatch) => {
        let workFlowPersist = sessionStorage.getItem('fm_workflow_crud_persist');
        if (workFlowPersist) {
            // coming from a workFlowAction, cancelled action, cancelled crud
            workFlowPersist = JSON.parse(workFlowPersist);
            const { data, entity, hasCrudInDetail } = workFlowPersist;
            dispatch(open(entity, data.id, hasCrudInDetail));
        } else {
            dispatch({ type: ENTITY_DETAIL_OPEN });
        }
    };
}

export function setNextDetail(entityType, data) {
    return (dispatch) => {
        dispatch({
            type: ENTITY_DETAIL_SET_NEXT_DETAIL,
            entityType,
            data,
        });
    };
}

export function reset() {
    return (dispatch) => {
        dispatch({ type: ENTITY_DETAIL_RESET });
    };
}

export function resetNextDetail() {
    return (dispatch) => {
        dispatch({ type: ENTITY_DETAIL_RESET_NEXT_DETAIL });
    };
}

export function changeSection(section) {
    return (dispatch) => {
        dispatch({ type: ENTITY_DETAIL_CHANGE_SECTION, section });
    };
}

export function updateDetailFields(entity, id, fields = {}) {
    return function (dispatch, getState) {
        const extraId = resolveEntityDetailExtraId(getState().entityDetail, entity.entity, id);
        dispatch({
            type: ENTITY_UPDATE_LOCALLY,
            entity: entity.entity,
            id,
            extraId,
            fields,
        });
    };
}

export function updateDetailExtra(entity, id, fields = {}) {
    return function (dispatch, getState) {
        const extraId = resolveEntityDetailExtraId(getState().entityDetail, entity.entity, id);
        dispatch({
            type: ENTITY_UPDATE_EXTRA,
            entity: entity.entity,
            id,
            extraId,
            fields,
        });
    };
}

export function setActiveTab({ activeKey, deletedKey, tabs }) {
    return function (dispatch) {
        dispatch({
            type: ENTITY_DETAIL_SET_TABS,
            active: activeKey,
            deletedKey,
            tabs,
        });
    };
}

export function updateDetailField(entity, fieldId, newValue, additionalFields = {}) {
    return (dispatch) => {
        const dataToUpdate = {
            ...additionalFields,
            [fieldId]: newValue.value,
        };
        return dispatch(Context.actions.EntityCrudActions.changeFields(dataToUpdate)).then(() => {
            dispatch(
                Context.actions.EntityCrudActions.save({
                    entity,
                    openDetail: true,
                    hasCrudInDetail: true,
                    fieldsToUpdate: dataToUpdate,
                }),
            );
        });
    };
}
